import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryProvider } from '@/components/providers/QueryProvider';
import { ThemeProvider } from '@/components/providers/ThemeProvider';
import { ApiClientInitializer } from '@/components/auth/ApiClientInitializer';
import { routeTree } from './routeTree.gen';

export interface RouterAuthContext {
  isLoading: boolean;
  isAuthenticated: boolean;
}

// Context is initialized with undefined! because InnerApp provides the real
// value via <RouterProvider context={...}>. This is the standard TanStack Router
// pattern for typed context — see https://tanstack.com/router/latest/docs/framework/react/guide/router-context
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  context: {
    auth: undefined!,
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return (
    <ClerkProvider
      publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
      afterSignOutUrl="/login"
    >
      <QueryProvider>
        <ApiClientInitializer />
        <ThemeProvider>
          <InnerApp />
        </ThemeProvider>
      </QueryProvider>
    </ClerkProvider>
  );
}

function InnerApp() {
  const { isLoaded, isSignedIn } = useAuth();
  return (
    <RouterProvider
      router={router}
      context={{ auth: { isLoading: !isLoaded, isAuthenticated: !!isSignedIn } }}
    />
  );
}
